import { NewReservationAction } from "~/reducers/newReservation"
import ILineItem from "~/interfaces/ILineItem"
import { LineItemParams, mapLineItemToParams } from "~/api2/orders"
import { LONG_TERM_RESERVATION_KINDS, RESERVATION_KINDS } from "~/constants"
import { GROUP_PAYMENT_TYPES } from "~/constants/group_payment_types"
import moment, { Moment } from "moment"
import { bySort } from "~/utils/sorts"
import IBookingInterval from "~/interfaces/IBookingInterval"
import { IReservationState } from "~/interfaces/IReservationStates"


const isIntervalAvailable = (
  interval: IBookingInterval,
  reservationStates: Record<string, IReservationState> | undefined,
  siteId: string | undefined,
  startDate: Moment | null | undefined
): boolean => {
  if (!reservationStates || !siteId || !startDate) {
    return true
  }

  const dayStart = startDate.clone().startOf("day")
  const intervalStart = dayStart.clone()
    .hour(parseInt(interval.start_time.slice(0, 2)))
    .minute(parseInt(interval.start_time.slice(3)))
  
  const intervalEnd = dayStart.clone()
    .hour(parseInt(interval.end_time.slice(0, 2)))
    .minute(parseInt(interval.end_time.slice(3)))
  
  // Handle overnight intervals (end time before start time)
  if (intervalEnd.isBefore(intervalStart)) {
    intervalEnd.add(1, "day")
  }

  // Get existing reservations for this site on the selected date
  const existingReservations = Object.values(reservationStates)
    .map(r => r.persisted)
    .filter(r => r && r.site_id === siteId)
    .map(r => ({ 
      start: moment(r.start_date), 
      end: moment(r.end_date) 
    }))
    .filter(r => r.start.isSame(dayStart, "day"))

  return !existingReservations.some(existing => 
    intervalStart.isBefore(existing.end) && existing.start.isBefore(intervalEnd)
  )
}

const reservationTypeExplainer = (timing: string): string => {
  switch (timing) {
    case RESERVATION_KINDS.RECURRING:
      return "Recurring reservations will break into monthly segments for sending invoices to long-term guests"
    case RESERVATION_KINDS.GROUP:
      return "Group reservations can hold multiple sites for a group and allow them to pay separately or together"
    case RESERVATION_KINDS.BLOCKED:
      return "Blocked reservations will prevent a site from being booked"
    case LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES:
      return "Used for monthly campers to track and send monthly invoices for rent and include electric charges"
    case LONG_TERM_RESERVATION_KINDS.RENT_ONLY:
      return "Used to automatically send invoices to campers where electric is included in the monthly price"
    case LONG_TERM_RESERVATION_KINDS.SEASONAL_ONLY:
      return "Used for seasonal or long-term campers that pay up front"
    case LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY:
      return "Used for seasonal or long-term campers that pay up front, and electric is billed monthly"
    case GROUP_PAYMENT_TYPES.SEPARATE:
      return "The leader will receive a link to share with the group to book individually"
    case GROUP_PAYMENT_TYPES.TOGETHER:
      return "The leader will pay for the group all at once"
    default:
      return "Single reservations are the standard use case for booking a guest in"
  }
}

const returnActionTypeFeeByProp = (key: string) => {
  switch (key) {
    case "is_holiday_rate_enabled":
      return NewReservationAction.UPDATE_IS_HOLIDAY_RATE_ENABLED
    case "is_passenger_fees_enabled":
      return NewReservationAction.UPDATE_IS_PASSENGER_FEES_ENABLED
    case "is_weekend_fee_enabled":
      return NewReservationAction.UPDATE_IS_WEEKEND_FEE_ENABLED
    case "is_pets_fee_enabled":
      return NewReservationAction.UPDATE_IS_PETS_FEE_ENABLED
    case "is_tax_enabled":
      return NewReservationAction.UPDATE_IS_TAX_ENABLED
    default:
      return NewReservationAction.UPDATE_IS_CREDIT_CARD_FEE_ENABLED
  }
}

const mapLineItemsToPriceDetailParams = (lineItems: Partial<ILineItem>[], isTaxEnabled: boolean): LineItemParams[] =>
  lineItems
    ?.filter((li) => li.add_on_id && li.add_on_id !== "change-me" && !li?.reservation_id && !li?.parent_line_item_id)
    ?.filter((li, index, self) => self.findIndex((item) => item.add_on_id === li.add_on_id) === index)
    ?.map((li) => mapLineItemToParams(li, isTaxEnabled))

export { reservationTypeExplainer, returnActionTypeFeeByProp, mapLineItemsToPriceDetailParams }

export const saveReaderToLocalStorage = (readerId, campId) => {
  if (readerId && campId) {
    localStorage.setItem(`park_terminal_reader_${campId}`, readerId)
  }
}

export const getSavedReaderFromLocalStorage = (campId) => {
  if (campId) {
    return localStorage.getItem(`park_terminal_reader_${campId}`)
  }
  return null
}

export const computeInitialTimes = (
  bookingIntervals: IBookingInterval[] | undefined,
  currentIntervalId: string | null | undefined,
  startDate: Moment | null | undefined,
  endDate: Moment | null | undefined,
  defaultHours: number,
  reservationStates?: Record<string, IReservationState> | undefined,
  siteId?: string | undefined
): { startTime: string; endTime: string; selectedIntervalId: string | null; shouldSkipAvailabilityCall?: boolean } => {
  const sorted = bookingIntervals?.slice().sort(bySort) ?? []
  const found = sorted.find((bi) => bi.id === currentIntervalId)

  // Check if multiple booking intervals are occupied - if so, find first available hour slot
  if (sorted.length > 0 && reservationStates && siteId && startDate) {
    const occupiedIntervals = sorted.filter(interval => !isIntervalAvailable(interval, reservationStates, siteId, startDate))
    
    // If multiple intervals are occupied, find the first available 1-hour slot starting from 7am
    if (occupiedIntervals.length > 1) {
      const fmt = "HH:mm"
      const dayStart = startDate.clone().startOf("day")
      
      // Get existing reservations for this site on the selected date
      const existingReservations = Object.values(reservationStates)
        .map(r => r.persisted)
        .filter(r => r && r.site_id === siteId)
        .map(r => ({ 
          start: moment(r.start_date), 
          end: moment(r.end_date) 
        }))
        .filter(r => r.start.isSame(dayStart, "day"))

      // Helper function to check if a specific hour slot is available
      const isHourSlotAvailable = (startHour: number): boolean => {
        const slotStart = dayStart.clone().hour(startHour).minute(0)
        const slotEnd = slotStart.clone().add(1, "hour")
        
        return !existingReservations.some(existing => 
          slotStart.isBefore(existing.end) && existing.start.isBefore(slotEnd)
        )
      }

      // Find first available hour starting from 7am
      for (let hour = 5; hour < 23; hour++) { // Search from 5am to 10pm (We can adjust this later)
        if (isHourSlotAvailable(hour)) {
          const startTime = `${hour.toString().padStart(2, '0')}:00`
          const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`
          
          return {
            startTime,
            endTime,
            selectedIntervalId: null,
            shouldSkipAvailabilityCall: true, // Skip API call
          }
        }
      }
      
      // If no hour slots available, fall back to default
      return {
        startTime: "05:00",
        endTime: "06:00",
        selectedIntervalId: null,
        shouldSkipAvailabilityCall: true,
      }
    }
  }

  if (found && isIntervalAvailable(found, reservationStates, siteId, startDate)) {
    return {
      startTime: found.start_time,
      endTime: found.end_time,
      selectedIntervalId: found.id,
      shouldSkipAvailabilityCall: false,
    }
  }

  const fmt = "HH:mm"
  if (startDate && endDate) {
    const startTime = startDate.format(fmt)
    const endTime = endDate.format(fmt)
    
    const matchingInterval = sorted.find(interval => 
      interval.start_time === startTime && 
      interval.end_time === endTime &&
      isIntervalAvailable(interval, reservationStates, siteId, startDate)
    )
    
    if (matchingInterval) {
      return {
        startTime: matchingInterval.start_time,
        endTime: matchingInterval.end_time,
        selectedIntervalId: matchingInterval.id,
        shouldSkipAvailabilityCall: false,
      }
    }
    
  }

  // Find the first available interval (only if not multiple are occupied)
  if (sorted.length > 0) {
    const availableInterval = sorted.find(interval => isIntervalAvailable(interval, reservationStates, siteId, startDate))
    
    if (availableInterval) {
      return {
        startTime: availableInterval.start_time,
        endTime: availableInterval.end_time,
        selectedIntervalId: availableInterval.id,
        shouldSkipAvailabilityCall: false,
      }
    }
    
    // If no intervals are available, default to Custom instead of showing unavailable interval
    const defaultStart = moment().hours(9).minutes(0)
    return {
      startTime: defaultStart.format(fmt),
      endTime: defaultStart.clone().add(defaultHours || 3, "hours").format(fmt),
      selectedIntervalId: null,
      shouldSkipAvailabilityCall: true, // Skip API call when no intervals available
    }
  }

  // fallback to 09:00 + defaultHours
  const now = moment().hours(9).minutes(0)
  return {
    startTime: now.format(fmt),
    endTime: now.clone().add(defaultHours, "hours").format(fmt),
    selectedIntervalId: null,
    shouldSkipAvailabilityCall: false,
  }
}

export const validateBookingIntervalsAndFindNextAvailable = (
  bookingIntervals: IBookingInterval[] | undefined,
  reservationStates: Record<string, IReservationState> | undefined,
  siteId: string | undefined,
  startDate: Moment | null | undefined,
  campTimezone: string,
  currentStartTime?: string,
  currentEndTime?: string
): { 
  availableInterval: IBookingInterval | null
  shouldUpdateTimes: boolean
  newStartTime: string | null
  newEndTime: string | null
} => {
  if (!bookingIntervals?.length || !reservationStates || !siteId || !startDate) {
    return {
      availableInterval: null,
      shouldUpdateTimes: false,
      newStartTime: null,
      newEndTime: null
    }
  }

  const dayStart = startDate.clone().startOf("day")
  const sortedIntervals = bookingIntervals.slice().sort(bySort)

  // First, try to find an interval that matches the current start/end times if provided
  if (currentStartTime && currentEndTime) {
    const matchingInterval = sortedIntervals.find(interval => 
      interval.start_time === currentStartTime && 
      interval.end_time === currentEndTime &&
      isIntervalAvailable(interval, reservationStates, siteId, startDate)
    )
    
    if (matchingInterval) {
      return {
        availableInterval: matchingInterval,
        shouldUpdateTimes: false, // Don't update times since we found a match
        newStartTime: matchingInterval.start_time,
        newEndTime: matchingInterval.end_time
      }
    }
  }

  // Find the first available interval
  const availableInterval = sortedIntervals.find(interval => isIntervalAvailable(interval, reservationStates, siteId, startDate))

  if (!availableInterval) {
    const nextDay = dayStart.clone().add(1, "day")
    
    const nextDayReservations = Object.values(reservationStates)
      .map(r => r.persisted)
      .filter(r => r && r.site_id === siteId)
      .map(r => ({ 
        start: moment(r.start_date), 
        end: moment(r.end_date) 
      }))
      .filter(r => r.start.isSame(nextDay, "day"))

    const nextDayAvailableInterval = sortedIntervals.find(interval => {
      const intervalStart = nextDay.clone()
        .hour(parseInt(interval.start_time.slice(0, 2)))
        .minute(parseInt(interval.start_time.slice(3)))
      
      const intervalEnd = nextDay.clone()
        .hour(parseInt(interval.end_time.slice(0, 2)))
        .minute(parseInt(interval.end_time.slice(3)))
      
      if (intervalEnd.isBefore(intervalStart)) {
        intervalEnd.add(1, "day")
      }

      return !nextDayReservations.some(existing => 
        intervalStart.isBefore(existing.end) && existing.start.isBefore(intervalEnd)
      )
    })

    if (nextDayAvailableInterval) {
      return {
        availableInterval: nextDayAvailableInterval,
        shouldUpdateTimes: true,
        newStartTime: nextDayAvailableInterval.start_time,
        newEndTime: nextDayAvailableInterval.end_time
      }
    }
  }

  return {
    availableInterval: availableInterval || null,
    shouldUpdateTimes: !!availableInterval,
    newStartTime: availableInterval?.start_time || null,
    newEndTime: availableInterval?.end_time || null
  }
}

export const testBookingIntervalValidation = () => {
  const mockBookingIntervals: IBookingInterval[] = [
    {
      id: "1",
      label: "Morning",
      icon_url: "",
      start_time: "09:00",
      end_time: "12:00",
      camping_style_id: "1",
      sort: 1,
      can_be_multiple_days: false
    },
    {
      id: "2", 
      label: "Afternoon",
      icon_url: "",
      start_time: "13:00",
      end_time: "17:00",
      camping_style_id: "1",
      sort: 2,
      can_be_multiple_days: false
    }
  ]

  const mockReservationStates: Record<string, IReservationState> = {
    "res1": {
      persisted: {
        id: "res1",
        site_id: "site1",
        start_date: "2024-01-15T09:00:00Z",
        end_date: "2024-01-15T12:00:00Z"
      } as any
    }
  }

  const result = validateBookingIntervalsAndFindNextAvailable(
    mockBookingIntervals,
    mockReservationStates,
    "site1",
    moment("2024-01-15"),
    "UTC"
  )

  return result
}

/**
 * Finds a matching booking interval for given start and end times
 * Returns the interval ID if a match is found, or null if no match (indicating "Custom")
 */
export const findMatchingBookingInterval = (
  bookingIntervals: IBookingInterval[] | undefined,
  startTime: string,
  endTime: string,
  reservationStates?: Record<string, IReservationState> | undefined,
  siteId?: string | undefined,
  startDate?: Moment | null | undefined
): string | null => {
  if (!bookingIntervals?.length) {
    return null
  }

  // Find an interval that matches the exact start and end times and is available
  const matchingInterval = bookingIntervals.find(interval => 
    interval.start_time === startTime && 
    interval.end_time === endTime &&
    isIntervalAvailable(interval, reservationStates, siteId, startDate)
  )

  return matchingInterval ? matchingInterval.id : null
}

